import {
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  ClickAwayListener,
  Divider,
  MenuList,
  Paper,
  Popper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CustomButton from "../CustomButton";
import ConfirmationModal from "../misc/ConfirmationModal";
import themeConfig from "../../theme/themeConfig.json";
import {
  ActionMenuItem,
  ActionMenuProps,
  ResolvedActionMenuItem,
} from "./actionMenu.interface";
import ActionMenuOption from "./ActionMenuOption";
import ActionMenuButton from "./ActionMenuButton";
import {
  ActionMenuComponentContext,
  CapturedActionButton,
} from "./ActionMenuComponentContext";

export type {
  ActionMenuItem,
  ActionMenuKind,
  ActionMenuProps,
  ActionMenuSection,
} from "./actionMenu.interface";

let nextMenuId = 0;

function needsDivider(item: ActionMenuItem, previous?: ActionMenuItem) {
  if (!previous) return false;
  return Boolean(
    item.dividerBefore ||
      (item.section && previous.section && item.section !== previous.section),
  );
}

function ActionMenuComponentMount({
  item,
  register,
  restoreFocus,
  getTrigger,
}: {
  item: ActionMenuItem;
  register: (id: string, button: CapturedActionButton | null) => void;
  restoreFocus: () => void;
  getTrigger: (id: string) => HTMLElement | null;
}) {
  const context = useMemo(
    () => ({
      register: (button: CapturedActionButton | null) =>
        register(item.id, button),
      restoreFocus,
      getTrigger: () => getTrigger(item.id),
    }),
    [item.id, register, restoreFocus, getTrigger],
  );

  return (
    <ActionMenuComponentContext.Provider value={context}>
      {item.component}
    </ActionMenuComponentContext.Provider>
  );
}

function ActionMenu({
  items,
  title,
  tooltipSize = "compact",
  minItemsForMenu,
  ariaLabel = "Actiuni",
  disabled = false,
}: ActionMenuProps) {
  const navigate = useNavigate();
  const [menuId] = useState(() => `action-menu-${++nextMenuId}`);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeTooltipId, setActiveTooltipId] = useState<string | null>(null);
  const [confirmationItem, setConfirmationItem] =
    useState<ActionMenuItem | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const menuTriggerRef = useRef<HTMLElement | null>(null);
  const inlineTriggerRefs = useRef<Record<string, HTMLElement | null>>({});
  const lastActionIdRef = useRef<string | null>(null);
  const lastConfirmationRef = useRef<ActionMenuItem | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [capturedButtons, setCapturedButtons] = useState<
    Record<string, CapturedActionButton>
  >({});
  const registerCapturedButton = useCallback(
    (id: string, button: CapturedActionButton | null) => {
      setCapturedButtons((current) => {
        if (current[id] === button) return current;
        const next = { ...current };
        if (button) next[id] = button;
        else delete next[id];
        return next;
      });
    },
    [],
  );
  const registerInlineTrigger = useCallback(
    (id: string, element: HTMLElement | null) => {
      if (element) inlineTriggerRefs.current[id] = element;
      else delete inlineTriggerRefs.current[id];
    },
    [],
  );
  const getTrigger = useCallback((id?: string) => {
    const inline =
      inlineTriggerRefs.current[id ?? lastActionIdRef.current ?? ""];
    if (inline?.isConnected) return inline;
    if (menuTriggerRef.current?.isConnected) return menuTriggerRef.current;
    if (triggerRef.current?.isConnected) return triggerRef.current;
    return (
      Object.values(inlineTriggerRefs.current).find(
        (element) => element?.isConnected,
      ) ?? null
    );
  }, []);
  const restoreFocus = useCallback(() => {
    getTrigger()?.focus();
  }, [getTrigger]);
  const confirmationContext = useMemo(
    () => ({
      register: () => undefined,
      restoreFocus,
      getTrigger,
    }),
    [getTrigger, restoreFocus],
  );
  const visibleItems = items
    .filter((item) => !item.hidden)
    .map((item): ResolvedActionMenuItem => {
      if (!item.component) return item;
      const button = capturedButtons[item.id];
      return {
        ...item,
        capturedButton: button,
        useCapturedHandler: !item.withConfirmationModal,
        icon: item.icon ?? button?.icon,
        color: item.color ?? button?.color,
        variant: item.variant ?? button?.variant,
        tooltip: item.tooltip ?? button?.tooltipTitle,
        navigate: item.navigate ?? button?.navigate,
        disabled: item.disabled || !button || button.disabled,
        loading: item.loading || button?.loading,
        withConfirmationModal:
          item.withConfirmationModal ?? button?.withConfirmationModal,
        onClick: button?.onClick
          ? (event?: MouseEvent<HTMLElement>) =>
              button.onClick?.(event as MouseEvent<HTMLButtonElement>)
          : item.onClick,
      };
    });
  const hasVisibleItems = visibleItems.length > 0;
  const showInlineButtons =
    minItemsForMenu !== undefined && visibleItems.length < minItemsForMenu;
  const isLoading = visibleItems.some((item) => item.loading);
  const isBusy = disabled || isLoading;
  const isOpen = Boolean(anchorEl) && !showInlineButtons;
  const displayItems = isLoading
    ? visibleItems.map((item) => ({
        ...item,
        tooltip: item.loading
          ? "Actiunea este in curs."
          : "Asteapta finalizarea actiunii in curs.",
      }))
    : visibleItems;

  useEffect(() => {
    setAnchorEl(null);
    setActiveTooltipId(null);
  }, [showInlineButtons, hasVisibleItems]);

  const handleClose = (shouldRestoreFocus = false) => {
    setAnchorEl(null);
    setActiveTooltipId(null);
    if (shouldRestoreFocus) restoreFocus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (event.key === "Escape" || event.key === "Tab") {
      event.preventDefault();
      event.stopPropagation();
      handleClose(true);
    }
  };

  const handleSelect = (
    item: ResolvedActionMenuItem,
    event: MouseEvent<HTMLElement>,
  ) => {
    if (isBusy || item.disabled || item.loading) return;
    lastActionIdRef.current = item.id;
    triggerRef.current = anchorEl ?? event.currentTarget;
    handleClose(
      !item.withConfirmationModal && !item.navigate && !item.component,
    );
    if (item.withConfirmationModal && !item.useCapturedHandler) {
      lastConfirmationRef.current = item;
      setConfirmationItem(item);
      return;
    }
    // Ruleaza direct din click, inclusiv actiunile care deschid o fereastra.
    item.onClick?.(event);
  };

  const handleConfirmationClose = () => {
    setConfirmationItem(null);
  };

  const handleConfirm = () => {
    const item = visibleItems.find((item) => item.id === confirmationItem?.id);
    if (!item || item.hidden || item.disabled || item.loading || isBusy) return;
    const callback =
      item.withConfirmationModal?.modalRightButtonOnClick ?? item.onClick;
    callback?.();
    if (item.navigate) navigate(item.navigate);
  };
  const confirmation = (confirmationItem ?? lastConfirmationRef.current)
    ?.withConfirmationModal;

  return (
    <>
      {items
        .filter((item) => item.component)
        .map((item) => (
          <ActionMenuComponentMount
            key={item.id}
            item={item}
            register={registerCapturedButton}
            restoreFocus={restoreFocus}
            getTrigger={getTrigger}
          />
        ))}
      {!hasVisibleItems ? null : showInlineButtons ? (
        <div
          className="flex flex-wrap justify-end gap-3"
          role="group"
          aria-label={ariaLabel}>
          {displayItems.map((item) => (
            <ActionMenuButton
              key={item.id}
              item={item}
              registerTrigger={registerInlineTrigger}
              menuRef={menuRef}
              tooltipOpen={activeTooltipId === item.id}
              onTooltipOpen={() => setActiveTooltipId(item.id)}
              onTooltipClose={() =>
                setActiveTooltipId((current) =>
                  current === item.id ? null : current,
                )
              }
              disabled={isBusy || item.disabled || item.loading}
              onSelect={handleSelect}
            />
          ))}
        </div>
      ) : (
        <CustomButton
          buttonRef={(element) => {
            menuTriggerRef.current = element;
          }}
          variant="outlined"
          aria-label={ariaLabel}
          aria-haspopup="menu"
          aria-controls={isOpen ? menuId : undefined}
          aria-expanded={isOpen}
          disabled={disabled}
          loading={isLoading}
          onClick={(event) => {
            triggerRef.current = event.currentTarget;
            setAnchorEl(isOpen ? null : event.currentTarget);
          }}
          onKeyDown={(event) => {
            if (!isBusy && event.key === "ArrowDown") {
              event.preventDefault();
              triggerRef.current = event.currentTarget;
              setAnchorEl(event.currentTarget);
            }
          }}>
          <span
            aria-hidden="true"
            className="text-lg leading-none tracking-wider">
            ...
          </span>
        </CustomButton>
      )}
      {isOpen && (
        <Popper
          open
          role="presentation"
          anchorEl={anchorEl}
          placement="bottom-end"
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
          modifiers={[
            { name: "offset", options: { offset: [0, 8] } },
            {
              name: "flip",
              options: {
                fallbackPlacements: ["top-end", "bottom-start", "top-start"],
                padding: 8,
              },
            },
            {
              name: "preventOverflow",
              options: { padding: 8, altAxis: true },
            },
          ]}>
          <ClickAwayListener onClickAway={() => handleClose()}>
            <Paper
              ref={menuRef}
              elevation={0}
              sx={{
                width: 290,
                maxWidth: "calc(100vw - 16px)",
                maxHeight: "calc(100vh - 16px)",
                display: "flex",
                flexDirection: "column",
                border: "1px solid #E2E8F0",
                borderRadius: "10px",
                boxShadow: themeConfig.shadows.card,
                overflow: "hidden",
              }}>
              {title && (
                <Box
                  sx={{
                    px: 2.25,
                    py: 1.5,
                    borderBottom: "1px solid #E2E8F0",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    lineHeight: 1.5,
                    flexShrink: 0,
                    overflowWrap: "anywhere",
                  }}>
                  {title}
                </Box>
              )}
              <MenuList
                id={menuId}
                aria-label={ariaLabel}
                aria-busy={isLoading || undefined}
                autoFocus
                autoFocusItem={false}
                disabledItemsFocusable
                variant="menu"
                onKeyDown={handleKeyDown}
                sx={{
                  p: 0.75,
                  minHeight: 0,
                  maxHeight: "calc(100vh - 24px)",
                  overflowY: "auto",
                }}>
                {displayItems.flatMap((item, index) => [
                  ...(needsDivider(item, displayItems[index - 1])
                    ? [
                        <Divider
                          key={`divider-${item.id}`}
                          component="li"
                          sx={{ my: 0.75, mx: 1.5, borderColor: "#E2E8F0" }}
                        />,
                      ]
                    : []),
                  <ActionMenuOption
                    key={`item-${item.id}`}
                    item={item}
                    menuRef={menuRef}
                    tooltipSize={tooltipSize}
                    tooltipOpen={activeTooltipId === item.id}
                    onTooltipOpen={() => setActiveTooltipId(item.id)}
                    onTooltipClose={() =>
                      setActiveTooltipId((current) =>
                        current === item.id ? null : current,
                      )
                    }
                    disabled={isBusy || item.disabled || item.loading}
                    onSelect={handleSelect}
                  />,
                ])}
              </MenuList>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
      {confirmation && (
        <ActionMenuComponentContext.Provider value={confirmationContext}>
          <ConfirmationModal
            show={Boolean(confirmationItem)}
            handleClose={handleConfirmationClose}
            title={confirmation.modalTitle}
            subtitle={confirmation.modalSubtitle}
            leftButtonText={confirmation.modalLeftButtonText}
            rightButtonText={confirmation.modalRightButtonText}
            leftButtonOnClick={confirmation.modalLeftButtonOnClick}
            rightButtonOnClick={handleConfirm}
            leftButtonColor={confirmation.modalLeftButtonColor}
            rightButtonColor={confirmation.modalRightButtonColor}
            leftButtonVariant={confirmation.modalLeftButtonVariant}
            rightButtonVariant={confirmation.modalRightButtonVariant}
            modalSize={confirmation.modalSize}
          />
        </ActionMenuComponentContext.Provider>
      )}
    </>
  );
}

export default ActionMenu;
